import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Plus, Trash2, Save } from "lucide-react";
import { toast } from "sonner";

const CATEGORIES = ["Exterior", "Interior", "Pool", "Night vibe"];

export default function GalleryManager() {
  const [items, setItems] = useState([]);
  const [newUrl, setNewUrl] = useState("");
  const [newCat, setNewCat] = useState("Exterior");

  const load = () => api.get("/content/gallery").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const add = async () => {
    if (!newUrl) { toast.error("Add an image URL"); return; }
    try {
      await api.post("/content/gallery", { url: newUrl, category: newCat, caption: "", order: items.length });
      setNewUrl("");
      toast.success("Added");
      load();
    } catch { toast.error("Could not add"); }
  };

  const save = async (item) => {
    try { await api.put(`/content/gallery/${item.id}`, item); toast.success("Saved"); }
    catch { toast.error("Could not save"); }
  };

  const del = async (id) => {
    try { await api.delete(`/content/gallery/${id}`); toast.success("Removed"); load(); }
    catch { toast.error("Could not delete"); }
  };

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const reader = new FileReader();
    reader.onload = () => setNewUrl(reader.result);
    reader.readAsDataURL(f);
  };

  return (
    <div data-testid="gallery-manager">
      <div className="flex items-center justify-between mb-8">
        <div>
          <div className="text-xs uppercase tracking-[0.22em] text-sage-600">Manage</div>
          <h1 className="font-serif text-4xl text-earth-900 mt-2 font-light">Gallery</h1>
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-earth-100 p-6 mb-6 flex flex-wrap gap-3 items-center">
        <Input className="flex-1 min-w-[280px]" placeholder="Image URL (or upload)" value={newUrl} onChange={(e) => setNewUrl(e.target.value)} data-testid="gallery-new-url" />
        <input type="file" accept="image/*" onChange={onFile} className="text-sm" data-testid="gallery-new-file" />
        <select className="border border-earth-200 rounded-md px-3 py-2 text-sm" value={newCat} onChange={(e) => setNewCat(e.target.value)}>
          {CATEGORIES.map((c) => <option key={c}>{c}</option>)}
        </select>
        <Button onClick={add} className="bg-sage-500 text-white hover:bg-sage-600 rounded-full" data-testid="add-gallery-btn">
          <Plus className="w-4 h-4 mr-1" strokeWidth={1.5} /> Add
        </Button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        {items.map((g) => (
          <div key={g.id} className="bg-white rounded-2xl border border-earth-100 overflow-hidden flex flex-col" data-testid={`gallery-tile-${g.id}`}>
            <div className="aspect-[4/3] bg-earth-100">
              <img src={g.url} alt={g.caption} className="w-full h-full object-cover" />
            </div>
            <div className="p-4 flex flex-col gap-2">
              <select
                className="border border-earth-200 rounded-md px-2 py-1.5 text-sm"
                value={g.category}
                onChange={(e) => setItems(items.map((x) => x.id === g.id ? { ...x, category: e.target.value } : x))}
              >
                {CATEGORIES.map((c) => <option key={c}>{c}</option>)}
              </select>
              <Input placeholder="Caption" value={g.caption || ""} onChange={(e) => setItems(items.map((x) => x.id === g.id ? { ...x, caption: e.target.value } : x))} />
              <Input type="number" placeholder="Order" value={g.order} onChange={(e) => setItems(items.map((x) => x.id === g.id ? { ...x, order: Number(e.target.value) } : x))} />
              <div className="flex gap-2">
                <Button size="sm" onClick={() => save(g)} className="flex-1 bg-earth-900 text-white hover:bg-earth-800 rounded-full" data-testid={`gallery-save-${g.id}`}>
                  <Save className="w-3.5 h-3.5 mr-1" strokeWidth={1.5} /> Save
                </Button>
                <Button size="icon" variant="ghost" onClick={() => del(g.id)} data-testid={`gallery-del-${g.id}`}>
                  <Trash2 className="w-4 h-4 text-red-600" strokeWidth={1.5} />
                </Button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
